import type { BaseTicket, BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { ArrowSquareOut, CaretDown, CaretUp, ChatCircle } from "@phosphor-icons/react";
import { Badge, Button, Flex, ScrollArea, Text } from "@radix-ui/themes";

import { CommentsList } from "./CommentsList";

const STATUS_COLORS: Record<
  string,
  "green" | "orange" | "blue" | "gray" | "red"
> = {
  approved: "green",
  complete: "green",
  needs_review: "orange",
  wip: "blue",
  incomplete: "red",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  complete: "Complete",
  needs_review: "Needs Review",
  wip: "In Progress",
  incomplete: "Incomplete",
};

interface TicketDetailsDrawerProps {
  ticket: BaseTicket;
  comments: BaseTicketComment[];
  isOpen: boolean;
  onToggle: () => void;
  onCommentClick: (comment: BaseTicketComment) => void;
  activeCommentId?: string | null;
}

export function TicketDetailsDrawer({
  ticket,
  comments,
  isOpen,
  onToggle,
  onCommentClick,
  activeCommentId,
}: TicketDetailsDrawerProps) {
  const activeComments = (comments ?? []).filter((c) => !c.isDeleted);
  const status = ticket.status ?? "wip";

  if (!isOpen) {
    // Collapsed: floating button
    return (
      <button
        onClick={onToggle}
        className="absolute bottom-4 right-4 z-30 flex items-center gap-2 rounded-full border border-puntt-neutral-gray-5 bg-base-white px-3 py-2 shadow-lg transition-colors hover:bg-puntt-neutral-gray-2"
      >
        <ChatCircle size={16} className="text-puntt-accent-9" />
        <Text size="1" weight="medium" className="text-puntt-neutral-gray-11">
          Details
        </Text>
        {activeComments.length > 0 && (
          <Badge size="1" variant="solid" color="blue">
            {activeComments.length}
          </Badge>
        )}
        <CaretUp size={12} className="text-puntt-neutral-gray-9" />
      </button>
    );
  }

  // Expanded: sliding panel from bottom
  return (
    <div className="absolute inset-x-0 bottom-0 z-30 flex max-h-[45%] flex-col rounded-t-lg border-t border-puntt-neutral-gray-5 bg-base-white shadow-xl animate-pane-slide-up">
      {/* Drawer handle / header */}
      <Flex
        align="center"
        justify="between"
        className="shrink-0 cursor-pointer border-b border-puntt-neutral-gray-4 px-4 py-2"
        onClick={onToggle}
      >
        <Flex align="center" gap="2" className="min-w-0 flex-1">
          <Text
            size="2"
            weight="bold"
            className="truncate text-puntt-neutral-gray-12"
          >
            {ticket.title}
          </Text>
          <Badge
            size="1"
            variant="soft"
            color={STATUS_COLORS[status] ?? "gray"}
          >
            {STATUS_LABELS[status] ?? status}
          </Badge>
        </Flex>
        <Flex align="center" gap="1" className="shrink-0">
          <Button
            size="1"
            variant="ghost"
            asChild
            className="cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={`/tickets/${ticket._id}/view?version=0`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowSquareOut size={14} />
            </a>
          </Button>
          <CaretDown size={14} className="text-puntt-neutral-gray-9" />
        </Flex>
      </Flex>

      {/* Scrollable content */}
      <ScrollArea className="flex-1">
        <div className="p-4">
          <CommentsList
            comments={comments}
            activeCommentId={activeCommentId}
            onCommentClick={onCommentClick}
          />
          {activeComments.length === 0 && (
            <Text size="2" className="text-puntt-neutral-gray-9">
              No comments yet
            </Text>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
